import {
  useSnackbarController,
  type SelectionController,
  type Entity,
  useReferenceDialog,
  useDataSource,
  type EntityCollection,
} from "@firecms/core";
import { Button } from "@firecms/ui";
import { useCallback, useMemo, useState } from "react";
import { getEventPath } from "../utils";
import {
  buildEventsCollection,
  buildEventsPath,
} from "../collections/events";
import { PublishStatus } from "../collections/publish";
import { toEventCollectionId } from "../workspace/eventIdentity";
import { getModeCollectionIds } from "../workspace/adminMode";
import { useAdminWorkspace } from "../workspace/useAdminWorkspace";

type CopyButtonProps = {
  collection: EntityCollection;
  selectionController: SelectionController;
  label: string;
  loadingLabel: string;
};

export function CopyButton({
  collection,
  selectionController,
  label,
  loadingLabel,
}: CopyButtonProps) {
  const [loading, setLoading] = useState(false);
  const { mode } = useAdminWorkspace();
  const spaceId = useMemo(
    () => getModeCollectionIds(mode).space ?? null,
    [mode]
  );
  const snackbarController = useSnackbarController();
  const dataSource = useDataSource();

  const copyEntity = useCallback(
    (entity: Entity<any>, pathTo: string) => {
      const values = {
        ...entity.values,
        publishSettings: {
          status: PublishStatus.DRAFT,
          publishedAt: null,
        },
      };

      if (values.finalized) {
        values.finalized = false;
      }
      if (values.finalizeAt) {
        values.finalizeAt = null;
      }
      if (values.finalizedAt) {
        values.finalizedAt = null;
      }

      dataSource
        .saveEntity({
          path: pathTo,
          values,
          collection,
          status: "new",
        })
        .then(() => {
          snackbarController.open({
            type: "success",
            message: "Copied entity " + entity.id,
          });
        })
        .catch((error) => {
          snackbarController.open({
            type: "error",
            message: "Error copying entity " + entity.id,
          });
          console.error(error);
        });
    },
    [collection, dataSource, snackbarController]
  );

  const copyEntities = useCallback(
    async (entity: Entity<any> | null) => {
      if (!entity) {
        snackbarController.open({
          message: "Internal error",
          type: "warning",
        });
        return;
      }

      const targetEvent = toEventCollectionId(entity.path, entity.id);
      if (!targetEvent) {
        snackbarController.open({
          message: "Invalid target event path",
          type: "warning",
        });
        return;
      }

      setLoading(true);

      const subcollection = collection.path?.split("/").pop() ?? collection.id;

      for (const selectedEntity of selectionController.selectedEntities) {
        const pathTo = `${getEventPath(targetEvent)}/${subcollection}`;
        await copyEntity(selectedEntity, pathTo);
      }

      setLoading(false);
    },
    [
      copyEntity,
      collection.path,
      collection.id,
      selectionController.selectedEntities,
      snackbarController,
    ]
  );

  const countText = ` (${selectionController.selectedEntities.length})`;
  const permitted = selectionController.selectedEntities.length > 0;

  const referenceDialog = useReferenceDialog({
    path: spaceId ? buildEventsPath(spaceId) : "events",
    collection: spaceId
      ? buildEventsCollection(spaceId)
      : buildEventsCollection({ accountId: "", spaceId: "" }),
    multiselect: false,
    onSingleEntitySelected: copyEntities,
  });

  return (
    <Button
      onClick={referenceDialog.open}
      variant="outlined"
      disabled={loading || !permitted || !spaceId}
    >
      {loading ? `${loadingLabel}...` : label + countText}
    </Button>
  );
}
